import { lazy, Suspense, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { overlayEntries } from "@/mobile/backClose";
import { ChunkBoundary } from "@/components/ChunkFailed";
import { Icon } from "@/components/Icon";
import { onChunkError } from "@/lib/appUpdate";
import { CANDIDATE_PARAM, type CandidateModalState } from "./candidateLink";
import "./cardBoot.css";

// Карточка — отдельный кусок сборки: вместе с лентой переписки, звонком и
// окнами она тянет большую часть кода приложения, а хост живёт в шелле и на
// каждой странице. Грузится при первом открытии карточки за вкладку.
const CandidateCard = lazy(() =>
  import("./CandidateCard").then((m) => ({ default: m.CandidateCard }), onChunkError),
);

// `mobile` / `onOpenChat` — the phone version's card (see CandidateCard);
// `onOpenChat` gets the card's own close too: the chat may be right under it.
export function CandidateModalHost({
  mobile = false,
  onOpenChat,
}: {
  mobile?: boolean;
  onOpenChat?: (personId: string, closeCard: () => void) => void;
} = {}) {
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const id = sp.get(CANDIDATE_PARAM);

  const pushed = (location.state as CandidateModalState)?.candidateModal === true;
  const close = useCallback(() => {
    if (pushed) {
      // On the phone, windows over the card keep history entries above it
      // (mobile/backClose) — step over those too.
      navigate(-1 - overlayEntries());
      return;
    }
    // Opened by a direct link: nothing to go back to, drop the param in place.
    const next = new URLSearchParams(sp);
    next.delete(CANDIDATE_PARAM);
    setSp(next, { replace: true, state: location.state });
  }, [pushed, navigate, sp, setSp, location.state]);

  if (!id) return null;
  // Пока кусок едет — тот же белый лист с «Загрузка…», что у самой карточки до
  // ответа API (cardBoot.css). Кусок не пришёл — «Обновить» на том же листе.
  // Крестик — на обоих: лист во весь экран, а назад по истории можно не всегда
  // (карточку открыли по прямой ссылке).
  const sheet = "cc-boot" + (mobile ? " cc-boot--m" : "");
  const closeBtn = (
    <button type="button" className="cc-boot-close" onClick={close} title="Закрыть">
      <Icon name="close" size={16} />
    </button>
  );
  return (
    <ChunkBoundary key={id} className={sheet} title="Карточка не загрузилась" extra={closeBtn}>
      <Suspense
        fallback={
          <div className={sheet}>
            {closeBtn}
            Загрузка…
          </div>
        }
      >
        <CandidateCard
          key={id}
          id={id}
          onClose={close}
          mobile={mobile}
          onOpenChat={onOpenChat && ((personId) => onOpenChat(personId, close))}
        />
      </Suspense>
    </ChunkBoundary>
  );
}
